import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CheckCircle2, ClipboardEdit, FileText, Loader2, Plus, ShieldAlert } from "lucide-react";
import { Sparkles } from "lucide-react";
import PolicyLink from "@/components/PolicyLink";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api, { API_URL, formatApiError } from "@/lib/api";
import {
  ASSESSMENT_METHODS, ASSESSOR_RECOMMENDATIONS, COMMENT_REQUIRED_RESPONSES, DIRECTION_LABELS,
  EMERGENCY_SCENARIOS, FA_STATUS_COLORS, FA_STATUS_LABELS, FINAL_COMPETENCY_AREAS, FINAL_RESPONSES,
  MANAGER_FINAL_DECISIONS, OBJECTIVE_FINAL_STATUSES,
} from "@/config/finalCatalog";
import { ComparisonTable } from "@/components/child/FinalAssessmentTab";

const SUMMARY_FIELDS = [
  ["strengths", "Strengths demonstrated *"], ["skills_achieved", "Skills achieved"],
  ["remaining_needs", "Remaining training needs"], ["safety_concerns", "Safety concerns"],
  ["independence_demonstrated", "Independence demonstrated"], ["emergency_skills", "Emergency skills"],
  ["relevant_interventions", "Relevant interventions"], ["outstanding_risks", "Outstanding risks"],
  ["reason", "Reason for recommendation *"],
];

const EMPTY_INTERVENTION = { journey_stage: "", location: "", what_happened: "", why_necessary: "", action_taken: "", outcome: "", safety_significance: "", further_training_required: "", risk_review_required: false, incident_required: false };

export default function FinalAssessmentPage() {
  const { assessmentId } = useParams();
  const { hasPermission } = useAuth();
  const canApprove = hasPermission("final:approve");
  const [fa, setFa] = useState(null);
  const [denied, setDenied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingToCoord, setSendingToCoord] = useState(false);
  const [aiDrafting, setAiDrafting] = useState(false);
  const [missing, setMissing] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [reviewForm, setReviewForm] = useState({ decision: "", comments: "", approved_directions: [] });
  const [trainForm, setTrainForm] = useState({ areas: "", new_objectives: "", additional_sessions: "", review_date: "" });
  const [amendForm, setAmendForm] = useState({ reason: "", note: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/final-assessments/${assessmentId}`);
      setFa(data);
    } catch (err) {
      if (err?.response?.status === 404 || err?.response?.status === 403) setDenied(true);
      else toast.error(formatApiError(err));
    }
  }, [assessmentId]);

  useEffect(() => { load(); }, [load]);

  const draftSummary = async () => {
    setAiDrafting(true);
    try {
      const { data } = await api.post("/ai/draft-final-report", { final_assessment_id: assessmentId });
      patch({ assessor_summary: { ...(fa.assessor_summary || {}), ...data.draft } });
      toast.success("AI draft inserted into the assessor summary — review and edit before saving");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAiDrafting(false);
    }
  };

  const sendToCoordinator = async () => {
    setSendingToCoord(true);
    try {
      await api.post("/report-submissions", { report_type: "final_assessment", report_id: assessmentId });
      toast.success("Final report sent to the Co-ordinator");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingToCoord(false);
    }
  };

  const editable = fa && ["draft", "in_progress", "returned_for_amendment"].includes(fa.status);
  const patch = (fields) => setFa((f) => ({ ...f, ...fields }));
  const setComp = (area, item, value) =>
    patch({ competencies: { ...(fa.competencies || {}), [area]: { ...((fa.competencies || {})[area] || {}), items: { ...(((fa.competencies || {})[area] || {}).items || {}), [item]: value } } } });
  const setCompComment = (area, value) =>
    patch({ competencies: { ...(fa.competencies || {}), [area]: { ...((fa.competencies || {})[area] || {}), comments: value } } });
  const setEK = (key, field, value) =>
    patch({ emergency_knowledge: { ...(fa.emergency_knowledge || {}), [key]: { ...((fa.emergency_knowledge || {})[key] || {}), [field]: value } } });
  const setSummary = (k, v) => patch({ assessor_summary: { ...(fa.assessor_summary || {}), [k]: v } });
  const setObjective = (id, field, value) =>
    patch({ objectives_review: (fa.objectives_review || []).map((o) => (o.objective_id === id ? { ...o, [field]: value } : o)) });
  const setFeedback = (section, k, v) => patch({ [section]: { ...(fa[section] || {}), [k]: v } });
  const setIntervention = (i, k, v) =>
    patch({ interventions: (fa.interventions || []).map((iv, j) => (j === i ? { ...iv, [k]: v } : iv)) });

  const save = async (submit = false) => {
    setSaving(true);
    setMissing([]);
    try {
      await api.patch(`/final-assessments/${assessmentId}`, {
        assessment_date: fa.assessment_date, start_time: fa.start_time, end_time: fa.end_time, location: fa.location,
        competencies: fa.competencies, interventions: fa.interventions, emergency_knowledge: fa.emergency_knowledge,
        objectives_review: fa.objectives_review, young_person_feedback: fa.young_person_feedback,
        parent_feedback: fa.parent_feedback, school_feedback: fa.school_feedback, assessor_summary: fa.assessor_summary,
      });
      if (submit) {
        await api.post(`/final-assessments/${assessmentId}/submit`);
        toast.success("Final assessment submitted for manager review");
      } else {
        toast.success("Saved");
      }
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.missing) setMissing(detail.missing);
      toast.error(typeof detail === "object" ? detail.message : formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const act = async (fn, success) => {
    setSaving(true);
    try {
      await fn();
      toast.success(success);
      setDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (denied) {
    return (
      <div data-testid="final-access-denied" className="flex flex-col items-center justify-center py-20 text-center">
        <ShieldAlert className="h-10 w-10 text-red-600 mb-4" />
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Access denied</h1>
        <p className="text-sm text-slate-600 max-w-md">This assessment does not exist or you are not authorised. This attempt has been recorded.</p>
      </div>
    );
  }
  if (!fa) {
    return <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading assessment…</div>;
  }

  const rs = fa.readiness_summary || {};

  return (
    <div className="space-y-4 pb-28" data-testid="final-page">
      <Link to={`/children/${fa.child_id}`} className="inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid="final-back-link">
        <ArrowLeft className="h-4 w-4" /> Back to {fa.child?.child_name}
      </Link>

      <div className="rounded-lg border border-border bg-white p-5" data-testid="final-header">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="final-number">{fa.final_number}</h1>
          <PolicyLink module="final" />
          <Badge className="bg-slate-100 text-slate-600 border-transparent">Attempt {fa.attempt}</Badge>
          <Badge className="bg-blue-50 text-blue-700 border-transparent">{DIRECTION_LABELS[fa.direction]}</Badge>
          <Badge className={FA_STATUS_COLORS[fa.status]} data-testid="final-status">{FA_STATUS_LABELS[fa.status]}</Badge>
        </div>
        <p className="mt-1 text-sm text-slate-600">
          {fa.child?.child_name} · {fa.child?.case_id} · Assessor: {fa.assessor_name} (automatically recorded)
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {editable && (
            <>
              <Button variant="outline" className="h-11" data-testid="final-save" disabled={saving} onClick={() => save(false)}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
              <Button className="h-11 bg-ukgreen hover:bg-ukgreen/90 text-white font-semibold" data-testid="final-submit" disabled={saving} onClick={() => save(true)}>
                <CheckCircle2 className="h-4 w-4 mr-1" /> Submit for manager review
              </Button>
            </>
          )}
          {!editable && (
            <>
              <a href={`${API_URL}/final-assessments/${fa.id}/pdf`} target="_blank" rel="noreferrer" data-testid="final-pdf-button"
                className="inline-flex h-11 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-brand-hover">
                <FileText className="h-4 w-4" /> Assessment PDF
              </a>
              <Button variant="outline" className="h-11" data-testid="final-send-coordinator" disabled={sendingToCoord} onClick={sendToCoordinator}>
                {sendingToCoord ? "Sending…" : "Send report to Co-ordinator"}
              </Button>
              {fa.can_amend && (
                <Button variant="outline" className="h-11" data-testid="final-amend-button" onClick={() => setDialog("amend")}>
                  <ClipboardEdit className="h-4 w-4 mr-1" /> Add amendment
                </Button>
              )}
              {canApprove && fa.status === "further_assessment_required" && (
                <Button variant="outline" className="h-11 border-amber-300 text-amber-800" data-testid="final-additional-training-button" onClick={() => setDialog("training")}>
                  Additional training plan
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {missing.length > 0 && (
        <div data-testid="final-completeness-errors" role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
          <p className="font-semibold">Assessment is incomplete — missing:</p>
          <ul className="list-disc pl-5">{missing.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}

      <Card data-testid="readiness-summary">
        <CardHeader><CardTitle className="text-base">Readiness summary (decision-support only)</CardTitle></CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><dt className="text-xs text-slate-500">Programme start</dt><dd>{rs.programme_start || "—"}</dd></div>
            <div><dt className="text-xs text-slate-500">Planned duration</dt><dd>{rs.planned_weeks ? `${rs.planned_weeks} weeks` : "—"}</dd></div>
            <div><dt className="text-xs text-slate-500">Sessions</dt><dd>{rs.sessions_completed ?? 0}/{rs.sessions_planned ?? 0} completed</dd></div>
            <div><dt className="text-xs text-slate-500">Objectives</dt><dd>{rs.objectives_achieved ?? 0}/{rs.objectives_total ?? 0} achieved</dd></div>
            <div><dt className="text-xs text-slate-500">Interventions</dt><dd>{rs.trainer_interventions ?? 0}</dd></div>
            <div><dt className="text-xs text-slate-500">Open incidents</dt><dd>{rs.open_incidents ?? 0}</dd></div>
            <div><dt className="text-xs text-slate-500">Route plan</dt><dd>{rs.journey_plan ? `${rs.journey_plan.plan_number} v${rs.journey_plan.version}` : "—"}</dd></div>
            <div><dt className="text-xs text-slate-500">Risk assessment</dt><dd>{rs.risk_assessment ? `${rs.risk_assessment.ra_number} v${rs.risk_assessment.version}` : "—"}</dd></div>
          </dl>
          <p className="mt-2 text-xs text-slate-500">{rs.note}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Initial vs final progress comparison</CardTitle></CardHeader>
        <CardContent>
          <ComparisonTable comparison={fa.comparison} />
          <p className="mt-2 text-xs text-slate-500">Derived from recorded independence-level history. The original approved Initial Assessment is preserved unchanged as historical evidence.</p>
        </CardContent>
      </Card>

      <fieldset disabled={!editable}>
        <Tabs defaultValue="competencies" className="w-full">
          <TabsList className="bg-white border border-border flex flex-wrap h-auto">
            <TabsTrigger value="competencies" data-testid="tab-fa-competencies">Observed Journey</TabsTrigger>
            <TabsTrigger value="emergency" data-testid="tab-fa-emergency">Emergency Knowledge</TabsTrigger>
            <TabsTrigger value="objectives" data-testid="tab-fa-objectives">Objectives</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="tab-fa-feedback">Feedback</TabsTrigger>
            <TabsTrigger value="summary" data-testid="tab-fa-summary">Assessor Summary</TabsTrigger>
          </TabsList>

          <TabsContent value="competencies" className="mt-4 space-y-4">
            <Card>
              <CardContent className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="space-y-1.5"><Label>Assessment date *</Label><Input data-testid="fa-date" type="date" value={fa.assessment_date || ""} onChange={(e) => patch({ assessment_date: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Start time *</Label><Input data-testid="fa-start" type="time" value={fa.start_time || ""} onChange={(e) => patch({ start_time: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>End time *</Label><Input data-testid="fa-end" type="time" value={fa.end_time || ""} onChange={(e) => patch({ end_time: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Location / route</Label><Input data-testid="fa-location" value={fa.location || ""} onChange={(e) => patch({ location: e.target.value })} /></div>
              </CardContent>
            </Card>
            {FINAL_COMPETENCY_AREAS.map((area) => (
              <Card key={area.key} data-testid={`fa-area-${area.key}`}>
                <CardHeader><CardTitle className="text-base">{area.label}</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {area.items.map((item) => {
                    const value = fa.competencies?.[area.key]?.items?.[item.key] || "";
                    return (
                      <div key={item.key} className="flex items-center gap-2">
                        <span className="flex-1 text-xs text-slate-700">{item.label}</span>
                        <Select value={value} onValueChange={(v) => setComp(area.key, item.key, v)}>
                          <SelectTrigger data-testid={`fa-${area.key}-${item.key}`} className="h-11 w-52">
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            {FINAL_RESPONSES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                  <Input placeholder={`${area.label} comments (required for significant support / intervention / not demonstrated)`}
                    data-testid={`fa-comment-${area.key}`}
                    value={fa.competencies?.[area.key]?.comments || ""} onChange={(e) => setCompComment(area.key, e.target.value)} />
                </CardContent>
              </Card>
            ))}

            <Card data-testid="fa-interventions">
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> Assessor interventions ({fa.interventions?.length ?? 0})</CardTitle>
                {editable && (
                  <Button variant="outline" size="sm" data-testid="fa-intervention-add"
                    onClick={() => patch({ interventions: [...(fa.interventions || []), { ...EMPTY_INTERVENTION }] })}>
                    <Plus className="h-4 w-4 mr-1" /> Add
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                {!fa.interventions?.length && <p className="text-sm text-slate-600">No interventions. An intervention never automatically determines the outcome.</p>}
                {fa.interventions?.map((iv, i) => (
                  <div key={i} className="rounded-md border border-amber-200 bg-amber-50/40 p-3 space-y-2" data-testid={`fa-intervention-${i}`}>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Journey stage" value={iv.journey_stage} onChange={(e) => setIntervention(i, "journey_stage", e.target.value)} data-testid={`fa-iv-stage-${i}`} />
                      <Input placeholder="Location" value={iv.location} onChange={(e) => setIntervention(i, "location", e.target.value)} data-testid={`fa-iv-location-${i}`} />
                    </div>
                    <Textarea rows={2} placeholder="What happened" value={iv.what_happened} onChange={(e) => setIntervention(i, "what_happened", e.target.value)} data-testid={`fa-iv-what-${i}`} />
                    <Textarea rows={2} placeholder="Why intervention was necessary" value={iv.why_necessary} onChange={(e) => setIntervention(i, "why_necessary", e.target.value)} data-testid={`fa-iv-why-${i}`} />
                    <div className="grid grid-cols-2 gap-2">
                      <Textarea rows={2} placeholder="Action taken" value={iv.action_taken} onChange={(e) => setIntervention(i, "action_taken", e.target.value)} data-testid={`fa-iv-action-${i}`} />
                      <Textarea rows={2} placeholder="Outcome / safety significance" value={iv.outcome} onChange={(e) => setIntervention(i, "outcome", e.target.value)} data-testid={`fa-iv-outcome-${i}`} />
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs">
                      <label className="flex items-center gap-1"><input type="checkbox" checked={iv.risk_review_required} onChange={(e) => setIntervention(i, "risk_review_required", e.target.checked)} data-testid={`fa-iv-risk-${i}`} /> Risk review required</label>
                      <label className="flex items-center gap-1"><input type="checkbox" checked={iv.incident_required} onChange={(e) => setIntervention(i, "incident_required", e.target.checked)} data-testid={`fa-iv-incident-${i}`} /> Incident report required</label>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="emergency" className="mt-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Emergency / contingency knowledge</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-slate-500">Assess by observation, discussion or scenario testing as professionally appropriate.</p>
                {EMERGENCY_SCENARIOS.map((sc) => {
                  const entry = fa.emergency_knowledge?.[sc.key] || {};
                  return (
                    <div key={sc.key} className="rounded-md border border-border p-3 space-y-2" data-testid={`fa-scenario-${sc.key}`}>
                      <p className="text-sm font-medium text-slate-800">{sc.label}</p>
                      <div className="flex flex-wrap gap-2">
                        <Select value={entry.response || ""} onValueChange={(v) => setEK(sc.key, "response", v)}>
                          <SelectTrigger data-testid={`fa-scenario-response-${sc.key}`} className="h-11 w-56"><SelectValue placeholder="Response" /></SelectTrigger>
                          <SelectContent className="bg-white">
                            {FINAL_RESPONSES.slice(0, 6).map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Select value={entry.method || ""} onValueChange={(v) => setEK(sc.key, "method", v)}>
                          <SelectTrigger data-testid={`fa-scenario-method-${sc.key}`} className="h-11 w-44"><SelectValue placeholder="Method" /></SelectTrigger>
                          <SelectContent className="bg-white">
                            {ASSESSMENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <Input placeholder="Comments" value={entry.comments || ""} onChange={(e) => setEK(sc.key, "comments", e.target.value)} data-testid={`fa-scenario-comments-${sc.key}`} />
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="objectives" className="mt-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Training objectives review (assessor-confirmed)</CardTitle></CardHeader>
              <CardContent className="space-y-3" data-testid="fa-objectives">
                {!fa.objectives_review?.length && <p className="text-sm text-slate-600">No programme objectives recorded.</p>}
                {fa.objectives_review?.map((o) => (
                  <div key={o.objective_id} className="rounded-md border border-border p-3 space-y-2" data-testid={`fa-objective-${o.objective_id}`}>
                    <p className="text-sm font-medium text-slate-900">{o.objective}</p>
                    <p className="text-xs text-slate-500">Starting position: {o.starting_ability || "—"} · Training status: {(o.training_status || "").replace(/_/g, " ")}</p>
                    <div className="flex flex-wrap gap-2">
                      <Select value={o.final_status || ""} onValueChange={(v) => setObjective(o.objective_id, "final_status", v)}>
                        <SelectTrigger data-testid={`fa-objective-status-${o.objective_id}`} className="h-11 w-56"><SelectValue placeholder="Final status *" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {OBJECTIVE_FINAL_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input className="flex-1 min-w-[180px]" placeholder="Evidence" value={o.evidence || ""} onChange={(e) => setObjective(o.objective_id, "evidence", e.target.value)} data-testid={`fa-objective-evidence-${o.objective_id}`} />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="feedback" className="mt-4 space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Young person's feedback</CardTitle></CardHeader>
              <CardContent className="space-y-2" data-testid="fa-young-person">
                <p className="text-xs text-slate-500">Accessible language. Self-reported confidence never solely determines the outcome.</p>
                {[["confident", "How confident do you feel about the journey?"], ["easiest", "What do you find easiest?"],
                  ["difficult", "What do you find difficult?"], ["if_wrong", "What would you do if something went wrong?"],
                  ["ready", "Do you feel ready to travel independently?"], ["worried", "Is there anything you are worried about?"],
                  ["comments", "Additional comments"]].map(([k, label]) => (
                  <div key={k} className="space-y-1">
                    <Label className="text-xs">{label}</Label>
                    <Textarea rows={2} value={fa.young_person_feedback?.[k] || ""} onChange={(e) => setFeedback("young_person_feedback", k, e.target.value)} data-testid={`fa-yp-${k}`} />
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Parent / carer feedback</CardTitle></CardHeader>
              <CardContent className="space-y-2" data-testid="fa-parent">
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Parent/carer name" value={fa.parent_feedback?.name || ""} onChange={(e) => setFeedback("parent_feedback", "name", e.target.value)} data-testid="fa-parent-name" />
                  <Input type="date" value={fa.parent_feedback?.date || ""} onChange={(e) => setFeedback("parent_feedback", "date", e.target.value)} data-testid="fa-parent-date" />
                </div>
                {[["view", "View of progress"], ["concerns", "Remaining concerns"], ["confidence", "Confidence in child's travel skills"], ["comments", "Additional information"]].map(([k, label]) => (
                  <Textarea key={k} rows={2} placeholder={label} value={fa.parent_feedback?.[k] || ""} onChange={(e) => setFeedback("parent_feedback", k, e.target.value)} data-testid={`fa-parent-${k}`} />
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">School / professional feedback</CardTitle></CardHeader>
              <CardContent className="space-y-2" data-testid="fa-school">
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="Professional" value={fa.school_feedback?.professional || ""} onChange={(e) => setFeedback("school_feedback", "professional", e.target.value)} data-testid="fa-school-name" />
                  <Input placeholder="Role" value={fa.school_feedback?.role || ""} onChange={(e) => setFeedback("school_feedback", "role", e.target.value)} data-testid="fa-school-role" />
                  <Input type="date" value={fa.school_feedback?.date || ""} onChange={(e) => setFeedback("school_feedback", "date", e.target.value)} data-testid="fa-school-date" />
                </div>
                <Textarea rows={2} placeholder="Feedback / observed progress" value={fa.school_feedback?.feedback || ""} onChange={(e) => setFeedback("school_feedback", "feedback", e.target.value)} data-testid="fa-school-feedback" />
                <Textarea rows={2} placeholder="Concerns" value={fa.school_feedback?.concerns || ""} onChange={(e) => setFeedback("school_feedback", "concerns", e.target.value)} data-testid="fa-school-concerns" />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="summary" className="mt-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Final assessor summary *</CardTitle></CardHeader>
              <CardContent className="space-y-3" data-testid="fa-summary">
                {editable && (
                  <Button type="button" variant="outline" className="h-10 border-dashed border-primary/50 text-primary" data-testid="fa-ai-draft" disabled={aiDrafting} onClick={draftSummary}>
                    <Sparkles className="h-4 w-4 mr-2" /> {aiDrafting ? "Drafting…" : "Draft summary with AI"}
                  </Button>
                )}
                {SUMMARY_FIELDS.map(([k, label]) => (
                  <div key={k} className="space-y-1">
                    <Label className="text-xs">{label}</Label>
                    <Textarea rows={2} value={fa.assessor_summary?.[k] || ""} onChange={(e) => setSummary(k, e.target.value)} data-testid={`fa-summary-${k}`} />
                  </div>
                ))}
                <div className="space-y-1">
                  <Label className="text-xs">Assessor recommendation *</Label>
                  <Select value={fa.assessor_summary?.recommended_outcome || ""} onValueChange={(v) => setSummary("recommended_outcome", v)}>
                    <SelectTrigger data-testid="fa-recommendation" className="h-11"><SelectValue placeholder="Select recommendation" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {ASSESSOR_RECOMMENDATIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">A recommendation only — the outcome is determined by authorised manager approval.</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </fieldset>

      {fa.manager_review && (
        <Card className="border-primary/30" data-testid="final-manager-review">
          <CardHeader><CardTitle className="text-base">Manager final review</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <p><b>{MANAGER_FINAL_DECISIONS.find((d) => d.value === fa.manager_review.decision)?.label}</b> by {fa.manager_review.reviewer_name} · {(fa.manager_review.at || "").slice(0, 16).replace("T", " ")}</p>
            {fa.manager_review.approved_directions?.length > 0 && <p>Directions approved: {fa.manager_review.approved_directions.map((d) => DIRECTION_LABELS[d]).join(", ")}</p>}
            <p className="text-slate-600">{fa.manager_review.comments}</p>
          </CardContent>
        </Card>
      )}

      {canApprove && fa.status === "awaiting_manager_review" && (
        <Card className="border-purple-200 bg-purple-50/40" data-testid="final-review-panel">
          <CardHeader><CardTitle className="text-base">Manager review</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-600">Assessed by {fa.assessor_name} — approval must come from a different authorised user (separation of duties).</p>
            <Select value={reviewForm.decision} onValueChange={(v) => setReviewForm((f) => ({ ...f, decision: v }))}>
              <SelectTrigger data-testid="final-review-decision" className="h-11 bg-white"><SelectValue placeholder="Select decision" /></SelectTrigger>
              <SelectContent className="bg-white">
                {MANAGER_FINAL_DECISIONS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {reviewForm.decision === "approve_direction_only" && (
              <div className="flex gap-3 text-sm">
                {["outbound", "return"].map((d) => (
                  <label key={d} className="flex items-center gap-1">
                    <input type="checkbox" data-testid={`final-review-dir-${d}`}
                      checked={reviewForm.approved_directions.includes(d)}
                      onChange={(e) => setReviewForm((f) => ({ ...f, approved_directions: e.target.checked ? [...f.approved_directions, d] : f.approved_directions.filter((x) => x !== d) }))} />
                    {DIRECTION_LABELS[d]}
                  </label>
                ))}
              </div>
            )}
            <Textarea data-testid="final-review-comments" rows={3} placeholder="Comments (required)" className="bg-white"
              value={reviewForm.comments} onChange={(e) => setReviewForm((f) => ({ ...f, comments: e.target.value }))} />
            <Button data-testid="final-review-submit" className="h-11 bg-primary text-white font-semibold" disabled={saving || !reviewForm.decision || !reviewForm.comments.trim()}
              onClick={() => act(() => api.post(`/final-assessments/${fa.id}/review`, reviewForm), "Decision recorded")}>Record decision</Button>
          </CardContent>
        </Card>
      )}

      {fa.amendments?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Amendment history</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {fa.amendments.map((a, i) => (
              <div key={i} className="rounded-md border border-border p-3 text-sm" data-testid={`final-amendment-${i}`}>
                <p className="font-medium">{a.reason}</p>
                {a.note && <p className="text-slate-600">{a.note}</p>}
                <p className="text-xs text-slate-500">{(a.at || "").slice(0, 16).replace("T", " ")} by {a.author_name}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Dialog open={dialog === "amend"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Add amendment</DialogTitle><DialogDescription>The original submitted assessment is never overwritten.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Reason *</Label><Input data-testid="final-amend-reason" value={amendForm.reason} onChange={(e) => setAmendForm((f) => ({ ...f, reason: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Amendment detail</Label><Textarea data-testid="final-amend-note" rows={3} value={amendForm.note} onChange={(e) => setAmendForm((f) => ({ ...f, note: e.target.value }))} /></div>
            <Button data-testid="final-amend-submit" className="h-11 w-full bg-primary text-white" disabled={saving || !amendForm.reason.trim()}
              onClick={() => act(() => api.post(`/final-assessments/${fa.id}/amend`, amendForm), "Amendment recorded")}>Record amendment</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "training"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Additional training plan</DialogTitle><DialogDescription>The programme reopens for further training. This assessment is preserved as a historical record; the next assessment becomes a new attempt.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Areas requiring further work *</Label><Textarea data-testid="training-areas" rows={2} value={trainForm.areas} onChange={(e) => setTrainForm((f) => ({ ...f, areas: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>New objectives (one per line)</Label><Textarea data-testid="training-objectives" rows={3} value={trainForm.new_objectives} onChange={(e) => setTrainForm((f) => ({ ...f, new_objectives: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5"><Label>Additional sessions</Label><Input data-testid="training-sessions" type="number" min="1" value={trainForm.additional_sessions} onChange={(e) => setTrainForm((f) => ({ ...f, additional_sessions: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Review date</Label><Input data-testid="training-review-date" type="date" value={trainForm.review_date} onChange={(e) => setTrainForm((f) => ({ ...f, review_date: e.target.value }))} /></div>
            </div>
            <Button data-testid="training-submit" className="h-11 w-full bg-primary text-white" disabled={saving || !trainForm.areas.trim()}
              onClick={() => act(() => api.post(`/final-assessments/${fa.id}/additional-training`, {
                ...trainForm,
                new_objectives: trainForm.new_objectives.split("\n").map((s) => s.trim()).filter(Boolean),
                additional_sessions: parseInt(trainForm.additional_sessions, 10) || null,
                review_date: trainForm.review_date || null,
              }), "Additional training plan created — programme reopened")}>Create plan</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
